import type {
  DpsBackfillOrchestrateResponse,
  DpsBulkReplayRequest,
  DpsBulkReplayResponse,
  DpsClickUpChannelsResponse,
  DpsDiagnosesListResponse,
  DpsDiagnosisDetail,
  DpsDiagnosisItem,
  DpsDiagnosisTimelineItem,
  DpsDiagnosisTimelineResponse,
  DpsEventHourlyTrendsResponse,
  DpsEventItem,
  DpsEventTrendsResponse,
  DpsEventsListResponse,
  DpsHealthResponse,
  DpsHostItem,
  DpsHostRole,
  DpsHostsListResponse,
  DpsLagInvestigationResult,
  DpsLagLiveResponse,
  DpsLagLiveSeriesResponse,
  DpsManualDiagnoseResponse,
  DpsManualLagDiagnoseRequest,
  DpsManualLagDiagnoseResponse,
  DpsServerHealthSnapshot,
  DpsServiceCorrelationResult,
  DpsNotificationPoliciesResponse,
  DpsNotificationPolicy,
  DpsNotificationPolicyUpdateInput,
  DpsNotificationPolicyOverride,
  DpsNotificationPolicyOverrideInput,
  DpsNotificationPolicyOverridesResponse,
  DpsNotifyResponse,
  DpsReplayResponse,
  DpsRulesetFamily,
  DpsRulesetFamiliesListResponse,
  DpsRulesetSchema,
  DpsRulesetValidationResult,
  DpsRulesetVersion,
  DpsRulesetVersionsListResponse,
  DpsServiceItem,
  DpsCreateHostInput,
  DpsCreateHostResponse,
  DpsEventGist,
  DpsEventGistFetchResult,
  DpsEventGistBuildResult,
  DpsIncidentDetailResponse,
  DpsIncidentsListResponse,
  DpsPipelineFetchResult,
  DpsPipelinePhaseView,
  DpsPipelineSnapshot,
  DpsSquadRouting,
  DpsSquadRoutingListResponse,
  DpsSquadRoutingUpsertInput,
  DpsSquadRoutingClickUpChannelsResponse,
} from './types';

export interface DpsApi {
  getHealth(): Promise<DpsHealthResponse>;
  checkAccess(): Promise<boolean>;
  checkAdmin(): Promise<boolean>;
  checkDefaultRulesetAdmin(): Promise<boolean>;
  listBackfillHosts(): Promise<string[]>;
  postBackfillOrchestrate(opts?: {
    days_back?: number;
    hostnames?: string[];
    catch_up?: boolean;
    from_date?: string;
    to_date?: string;
    environment?: string;
  }): Promise<DpsBackfillOrchestrateResponse>;
  postBulkReplay(body: DpsBulkReplayRequest): Promise<DpsBulkReplayResponse>;
  getLagRegion(options: {
    regionKey: string;
    environment?: string;
    at?: string;
  }): Promise<DpsLagLiveResponse>;
  getLagRegionSeries(options: {
    regionKey: string;
    environment?: string;
    end_at?: string;
    window_hours?: number;
  }): Promise<DpsLagLiveSeriesResponse>;
  postManualLagDiagnose(
    body: DpsManualLagDiagnoseRequest,
  ): Promise<DpsManualLagDiagnoseResponse>;
  listEvents(options?: {
    limit?: number;
    cursor?: string;
    node_id?: string;
    node_search?: string;
    node_prefix?: string;
    severity?: string;
    source?: string;
    status?: string;
    kind?: 'nss' | 'lag';
    breach_from?: string;
    breach_to?: string;
  }): Promise<DpsEventsListResponse>;
  listEventNodes(): Promise<string[]>;
  getEventTrends(
    days?: number,
    opts?: { kind?: 'nss' | 'lag' },
  ): Promise<DpsEventTrendsResponse>;
  getEventHourlyTrends(
    date: string,
    opts?: { kind?: 'nss' | 'lag' },
  ): Promise<DpsEventHourlyTrendsResponse>;
  getEvent(eventId: string): Promise<DpsEventItem>;
  getEventGist(eventId: string): Promise<DpsEventGistFetchResult>;
  triggerEventGistBuild(eventId: string): Promise<DpsEventGistBuildResult>;
  getIncident(incidentId: string): Promise<DpsIncidentDetailResponse>;
  listIncidents(options?: {
    limit?: number;
    cursor?: string;
    status?: 'open' | 'closed';
    node_search?: string;
    region_key?: string;
    environment?: string;
    started_from?: string;
    started_to?: string;
  }): Promise<DpsIncidentsListResponse>;
  listDiagnosesForEvent(
    eventId: string,
    options?: { limit?: number; cursor?: string },
  ): Promise<DpsDiagnosesListResponse>;
  getDiagnosis(diagnosisId: string): Promise<DpsDiagnosisDetail>;
  getRelatedDiagnoses(diagnosisId: string): Promise<DpsDiagnosisItem[]>;
  investigateLag(diagnosisId: string): Promise<DpsLagInvestigationResult>;
  correlateServices(diagnosisId: string): Promise<DpsServiceCorrelationResult>;
  notifyDiagnosis(
    diagnosisId: string,
    channel?: 'slack' | 'clickup',
    options?: { force?: boolean; destination?: string },
  ): Promise<DpsNotifyResponse>;
  listClickUpChannels(): Promise<DpsClickUpChannelsResponse>;
  listDiagnosisTimeline(
    diagnosisId: string,
    options?: { limit?: number; cursor?: string },
  ): Promise<DpsDiagnosisTimelineResponse>;
  listAllDiagnosisTimeline(
    diagnosisId: string,
  ): Promise<DpsDiagnosisTimelineItem[]>;
  listRulesetFamilies(): Promise<DpsRulesetFamiliesListResponse>;
  getRulesetFamily(id: string): Promise<DpsRulesetFamily>;
  listRulesetVersions(
    familyId: string,
  ): Promise<DpsRulesetVersionsListResponse>;
  getRulesetVersion(
    familyId: string,
    version: number,
  ): Promise<DpsRulesetVersion>;
  createRulesetFamily(body: {
    ruleset_family_id: string;
    description?: string;
    config?: Record<string, unknown>;
  }): Promise<DpsRulesetFamily>;
  createRulesetVersion(
    familyId: string,
    body: { config: Record<string, unknown>; activate?: boolean },
  ): Promise<DpsRulesetVersion>;
  updateRulesetFamily(
    id: string,
    body: { active_version?: number; description?: string },
  ): Promise<DpsRulesetFamily>;
  validateRulesetConfig(
    config: Record<string, unknown>,
  ): Promise<DpsRulesetValidationResult>;
  getRulesetSchema(): Promise<DpsRulesetSchema>;
  getRulesetTemplate(): Promise<Record<string, unknown>>;
  replayDiagnosis(
    eventId: string,
    body: {
      ruleset_family_id: string;
      ruleset_version: number;
      include_timeline?: boolean;
    },
  ): Promise<DpsReplayResponse>;
  deleteReplayDiagnosis(diagnosisId: string): Promise<void>;
  bulkDeleteReplays(body: {
    ruleset_family_id: string;
    ruleset_version: number;
  }): Promise<{
    status: string;
    job_id: string;
    created: boolean;
    ruleset_family_id: string;
    ruleset_version: number;
  }>;
  purgeQueue(queue?: string): Promise<{ purged: string[] }>;
  bulkDeletePrimaries(body: {
    from: string;
    to: string;
    node_id?: string;
  }): Promise<{
    status: string;
    job_id: string;
    created: boolean;
    from: string;
    to: string;
    node_id: string | null;
  }>;
  getJobStatus(jobId: string): Promise<{
    id: string;
    state: string;
    output: unknown;
    created_on: string;
    completed_on: string | null;
  } | null>;
  listHosts(): Promise<DpsHostItem[]>;
  updateHostRole(
    hostname: string,
    role: DpsHostRole | null,
  ): Promise<DpsHostItem>;
  createHost(input: DpsCreateHostInput): Promise<DpsCreateHostResponse>;
  deleteHost(hostname: string): Promise<{ deleted: boolean; hostname: string }>;
  diagnoseNode(nodeId: string): Promise<DpsManualDiagnoseResponse>;
  getService(entityRef: string): Promise<DpsServiceItem | null>;
  getNotificationPolicies(): Promise<DpsNotificationPolicy[]>;
  updateNotificationPolicy(
    role: string,
    input: DpsNotificationPolicyUpdateInput,
  ): Promise<DpsNotificationPolicy>;
  getNotificationPolicyOverrides(): Promise<DpsNotificationPolicyOverride[]>;
  upsertNotificationPolicyOverride(
    hostname: string,
    input: DpsNotificationPolicyOverrideInput,
  ): Promise<DpsNotificationPolicyOverride>;
  deleteNotificationPolicyOverride(hostname: string): Promise<void>;
  listSquadRouting(): Promise<DpsSquadRouting[]>;
  upsertSquadRouting(
    ownerRef: string,
    input: DpsSquadRoutingUpsertInput,
  ): Promise<DpsSquadRouting>;
  deleteSquadRouting(ownerRef: string): Promise<void>;
  /**
   * Lists live ClickUp channels (cached server-side for ~5 minutes) for the
   * Squad Routing admin UI's channel autocomplete. Returns
   * `{ items: [], configured: false }` when ClickUp is not configured for
   * this environment so the UI can fall back to free-text entry.
   */
  listSquadRoutingClickUpChannels(): Promise<DpsSquadRoutingClickUpChannelsResponse>;
  getServerHealth(eventId: string): Promise<DpsServerHealthSnapshot | null>;
  getEventPipeline(eventId: string): Promise<DpsPipelineFetchResult>;
}

function getBrowserTimezone(): string | undefined {
  try {
    return new Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return undefined;
  }
}

async function readJsonError(response: Response): Promise<string> {
  try {
    const data = (await response.json()) as {
      error?: { message?: string };
    };
    return data.error?.message ?? `Request failed (${response.status})`;
  } catch {
    return `Request failed (${response.status})`;
  }
}

export class DpsApiClient implements DpsApi {
  private readonly discoveryApi: {
    getBaseUrl(pluginId: string): Promise<string>;
  };
  private readonly fetchApi: { fetch: typeof fetch };

  constructor(options: {
    discoveryApi: { getBaseUrl(pluginId: string): Promise<string> };
    fetchApi?: { fetch: typeof fetch };
  }) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi ?? { fetch: fetch.bind(window) };
  }

  private async baseUrl(): Promise<string> {
    return this.discoveryApi.getBaseUrl('dps');
  }

  async getHealth(): Promise<DpsHealthResponse> {
    const baseUrl = await this.baseUrl();
    const response = await this.fetchApi.fetch(`${baseUrl}/health`);
    if (!response.ok) {
      throw new Error(await readJsonError(response));
    }
    return (await response.json()) as DpsHealthResponse;
  }

  async checkAccess(): Promise<boolean> {
    const baseUrl = await this.baseUrl();
    try {
      const response = await this.fetchApi.fetch(`${baseUrl}/v1/access/check`);
      return response.ok;
    } catch {
      return false;
    }
  }

  async checkAdmin(): Promise<boolean> {
    const baseUrl = await this.baseUrl();
    try {
      const response = await this.fetchApi.fetch(`${baseUrl}/v1/admin/check`);
      return response.ok;
    } catch {
      return false;
    }
  }

  async checkDefaultRulesetAdmin(): Promise<boolean> {
    const baseUrl = await this.baseUrl();
    try {
      const response = await this.fetchApi.fetch(
        `${baseUrl}/v1/rulesets/default-admin/check`,
      );
      return response.ok;
    } catch {
      return false;
    }
  }

  async listBackfillHosts(): Promise<string[]> {
    const baseUrl = await this.baseUrl();
    const response = await this.fetchApi.fetch(`${baseUrl}/v1/backfill/hosts`);
    if (!response.ok) {
      throw new Error(await readJsonError(response));
    }
    const data = (await response.json()) as { hostnames: string[] };
    return data.hostnames;
  }

  async postBackfillOrchestrate(opts?: {
    days_back?: number;
    hostnames?: string[];
    catch_up?: boolean;
    from_date?: string;
    to_date?: string;
    environment?: string;
  }): Promise<DpsBackfillOrchestrateResponse> {
    const baseUrl = await this.baseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/v1/backfill/orchestrate`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(opts ?? {}),
      },
    );
    const data = (await response.json()) as {
      error?: { message?: string };
      status?: string;
      run_id?: string;
      days_back?: number;
      job_id?: string;
      created?: boolean;
    };
    if (!response.ok) {
      throw new Error(
        data.error?.message ?? `Request failed (${response.status})`,
      );
    }
    return data as DpsBackfillOrchestrateResponse;
  }

  async postBulkReplay(
    body: DpsBulkReplayRequest,
  ): Promise<DpsBulkReplayResponse> {
    const baseUrl = await this.baseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/v1/admin/bulk-replay`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      },
    );
    if (!response.ok) {
      throw new Error(await readJsonError(response));
    }
    return (await response.json()) as DpsBulkReplayResponse;
  }

  async getLagRegion(options: {
    regionKey: string;
    environment?: string;
    at?: string;
  }): Promise<DpsLagLiveResponse> {
    const baseUrl = await this.baseUrl();
    const params = new URLSearchParams();
    if (options.environment) params.set('environment', options.environment);
    if (options.at) params.set('at', options.at);
    const qs = params.toString() ? `?${params.toString()}` : '';
    const response = await this.fetchApi.fetch(
      `${baseUrl}/v1/lag/region/${encodeURIComponent(options.regionKey)}${qs}`,
    );
    if (!response.ok) {
      throw new Error(await readJsonError(response));
    }
    return (await response.json()) as DpsLagLiveResponse;
  }

  async getLagRegionSeries(options: {
    regionKey: string;
    environment?: string;
    end_at?: string;
    window_hours?: number;
  }): Promise<DpsLagLiveSeriesResponse> {
    const baseUrl = await this.baseUrl();
    const params = new URLSearchParams();
    if (options.environment) params.set('environment', options.environment);
    if (options.end_at) params.set('end_at', options.end_at);
    if (options.window_hours !== undefined) {
      params.set('window_hours', String(options.window_hours));
    }
    const qs = params.toString() ? `?${params.toString()}` : '';
    const response = await this.fetchApi.fetch(
      `${baseUrl}/v1/lag/region/${encodeURIComponent(
        options.regionKey,
      )}/series${qs}`,
    );
    if (!response.ok) {
      throw new Error(await readJsonError(response));
    }
    return (await response.json()) as DpsLagLiveSeriesResponse;
  }

  async postManualLagDiagnose(
    body: DpsManualLagDiagnoseRequest,
  ): Promise<DpsManualLagDiagnoseResponse> {
    const baseUrl = await this.baseUrl();
    const response = await this.fetchApi.fetch(`${baseUrl}/v1/lag/diagnose`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!response.ok) {
      throw new Error(await readJsonError(response));
    }
    return (await response.json()) as DpsManualLagDiagnoseResponse;
  }

  async listEvents(options?: {
    limit?: number;
    cursor?: string;
    node_id?: string;
    node_search?: string;
    node_prefix?: string;
    severity?: string;
    source?: string;
    status?: string;
    kind?: 'nss' | 'lag';
    breach_from?: string;
    breach_to?: string;
  }): Promise<DpsEventsListResponse> {
    const baseUrl = await this.baseUrl();
    const params = new URLSearchParams();
    if (typeof options?.limit === 'number')
      params.set('limit', String(options.limit));
    if (options?.cursor) params.set('cursor', options.cursor);
    if (options?.node_id) params.set('node_id', options.node_id);
    if (options?.node_search) params.set('node_search', options.node_search);
    if (options?.node_prefix) params.set('node_prefix', options.node_prefix);
    if (options?.severity) params.set('severity', options.severity);
    if (options?.source) params.set('source', options.source);
    if (options?.status) params.set('status', options.status);
    if (options?.kind) params.set('kind', options.kind);
    if (options?.breach_from) params.set('breach_from', options.breach_from);
    if (options?.breach_to) params.set('breach_to', options.breach_to);
    const q = params.toString();
    const url = `${baseUrl}/v1/events${q ? `?${q}` : ''}`;
    const response = await this.fetchApi.fetch(url);
    if (!response.ok) {
      throw new Error(await readJsonError(response));
    }
    return (await response.json()) as DpsEventsListResponse;
  }

  async listEventNodes(): Promise<string[]> {
    const baseUrl = await this.baseUrl();
    const response = await this.fetchApi.fetch(`${baseUrl}/v1/events/nodes`);
    if (!response.ok) {
      throw new Error(await readJsonError(response));
    }
    const data = (await response.json()) as { nodes: string[] };
    return data.nodes;
  }

  async getEventTrends(
    days?: number,
    opts?: { kind?: 'nss' | 'lag' },
  ): Promise<DpsEventTrendsResponse> {
    const baseUrl = await this.baseUrl();
    const params = new URLSearchParams();
    if (typeof days === 'number') params.set('days', String(days));
    if (opts?.kind) params.set('kind', opts.kind);
    const q = params.toString();
    const url = `${baseUrl}/v1/events/trends${q ? `?${q}` : ''}`;
    const response = await this.fetchApi.fetch(url);
    if (!response.ok) {
      throw new Error(await readJsonError(response));
    }
    return (await response.json()) as DpsEventTrendsResponse;
  }

  async getEventHourlyTrends(
    date: string,
    opts?: { kind?: 'nss' | 'lag' },
  ): Promise<DpsEventHourlyTrendsResponse> {
    const baseUrl = await this.baseUrl();
    const params = new URLSearchParams();
    params.set('date', date);
    const tz = getBrowserTimezone();
    if (tz) params.set('tz', tz);
    if (opts?.kind) params.set('kind', opts.kind);
    const url = `${baseUrl}/v1/events/trends/hourly?${params.toString()}`;
    const response = await this.fetchApi.fetch(url);
    if (!response.ok) {
      throw new Error(await readJsonError(response));
    }
    return (await response.json()) as DpsEventHourlyTrendsResponse;
  }

  async getEvent(eventId: string): Promise<DpsEventItem> {
    const baseUrl = await this.baseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/v1/events/${encodeURIComponent(eventId)}`,
    );
    if (!response.ok) {
      throw new Error(await readJsonError(response));
    }
    return (await response.json()) as DpsEventItem;
  }

  async getEventGist(eventId: string): Promise<DpsEventGistFetchResult> {
    const baseUrl = await this.baseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/v1/event-gists/${encodeURIComponent(eventId)}`,
    );
    if (response.ok) {
      const body = (await response.json()) as { gist: DpsEventGist };
      return { status: 'ok', gist: body.gist };
    }
    if (response.status === 404) {
      let buildStatus: string | null = null;
      try {
        const body = (await response.clone().json()) as {
          build_status?: string;
        };
        buildStatus = body?.build_status ?? null;
      } catch {
        buildStatus = null;
      }
      if (!buildStatus) {
        buildStatus = response.headers.get('X-Dps-Gist-Status');
      }
      if (buildStatus === 'building') {
        return { status: 'building' };
      }
      return { status: 'unavailable' };
    }
    if (response.status === 503) {
      return { status: 'unavailable', error: 'Database not configured' };
    }
    return { status: 'error', error: await readJsonError(response) };
  }

  async triggerEventGistBuild(
    eventId: string,
  ): Promise<DpsEventGistBuildResult> {
    const baseUrl = await this.baseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/v1/event-gists/${encodeURIComponent(eventId)}/build`,
      { method: 'POST' },
    );
    if (response.ok) {
      return { status: 'enqueued' };
    }
    if (response.status === 503) {
      return {
        status: 'unavailable',
        error: await readJsonError(response),
      };
    }
    return { status: 'error', error: await readJsonError(response) };
  }

  async getIncident(incidentId: string): Promise<DpsIncidentDetailResponse> {
    const baseUrl = await this.baseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/v1/incidents/${encodeURIComponent(incidentId)}`,
    );
    if (!response.ok) {
      throw new Error(await readJsonError(response));
    }
    return (await response.json()) as DpsIncidentDetailResponse;
  }

  async listIncidents(options?: {
    limit?: number;
    cursor?: string;
    status?: 'open' | 'closed';
    node_search?: string;
    region_key?: string;
    environment?: string;
    started_from?: string;
    started_to?: string;
  }): Promise<DpsIncidentsListResponse> {
    const baseUrl = await this.baseUrl();
    const params = new URLSearchParams();
    if (typeof options?.limit === 'number')
      params.set('limit', String(options.limit));
    if (options?.cursor) params.set('cursor', options.cursor);
    if (options?.status) params.set('status', options.status);
    if (options?.node_search) params.set('node_search', options.node_search);
    if (options?.region_key) params.set('region_key', options.region_key);
    if (options?.environment) params.set('environment', options.environment);
    if (options?.started_from) params.set('started_from', options.started_from);
    if (options?.started_to) params.set('started_to', options.started_to);
    const q = params.toString();
    const url = `${baseUrl}/v1/incidents${q ? `?${q}` : ''}`;
    const response = await this.fetchApi.fetch(url);
    if (!response.ok) {
      throw new Error(await readJsonError(response));
    }
    return (await response.json()) as DpsIncidentsListResponse;
  }

  async listDiagnosesForEvent(
    eventId: string,
    options?: { limit?: number; cursor?: string },
  ): Promise<DpsDiagnosesListResponse> {
    const baseUrl = await this.baseUrl();
    const params = new URLSearchParams();
    if (typeof options?.limit === 'number')
      params.set('limit', String(options.limit));
    if (options?.cursor) params.set('cursor', options.cursor);
    const q = params.toString();
    const url = `${baseUrl}/v1/events/${encodeURIComponent(eventId)}/diagnoses${
      q ? `?${q}` : ''
    }`;
    const response = await this.fetchApi.fetch(url);
    if (!response.ok) {
      throw new Error(await readJsonError(response));
    }
    return (await response.json()) as DpsDiagnosesListResponse;
  }

  async getDiagnosis(diagnosisId: string): Promise<DpsDiagnosisDetail> {
    const baseUrl = await this.baseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/v1/diagnoses/${encodeURIComponent(diagnosisId)}`,
    );
    if (!response.ok) {
      throw new Error(await readJsonError(response));
    }
    return (await response.json()) as DpsDiagnosisDetail;
  }

  async getRelatedDiagnoses(diagnosisId: string): Promise<DpsDiagnosisItem[]> {
    const baseUrl = await this.baseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/v1/diagnoses/${encodeURIComponent(diagnosisId)}/related`,
    );
    if (!response.ok) {
      throw new Error(await readJsonError(response));
    }
    const body = (await response.json()) as { items?: DpsDiagnosisItem[] };
    return body.items ?? [];
  }

  async investigateLag(
    diagnosisId: string,
  ): Promise<DpsLagInvestigationResult> {
    const baseUrl = await this.baseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/v1/diagnoses/${encodeURIComponent(
        diagnosisId,
      )}/investigate-lag`,
      { method: 'POST' },
    );
    if (!response.ok) {
      throw new Error(await readJsonError(response));
    }
    return (await response.json()) as DpsLagInvestigationResult;
  }

  async correlateServices(
    diagnosisId: string,
  ): Promise<DpsServiceCorrelationResult> {
    const baseUrl = await this.baseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/v1/diagnoses/${encodeURIComponent(
        diagnosisId,
      )}/correlate-services`,
      { method: 'POST' },
    );
    if (!response.ok) {
      throw new Error(await readJsonError(response));
    }
    return (await response.json()) as DpsServiceCorrelationResult;
  }

  async notifyDiagnosis(
    diagnosisId: string,
    channel: 'slack' | 'clickup' = 'slack',
    options?: { force?: boolean; destination?: string },
  ): Promise<DpsNotifyResponse> {
    const baseUrl = await this.baseUrl();
    const params = new URLSearchParams();
    if (channel !== 'slack') params.set('channel', channel);
    if (options?.force) params.set('force', 'true');
    if (options?.destination) params.set('destination', options.destination);
    const qs = params.toString() ? `?${params.toString()}` : '';
    const response = await this.fetchApi.fetch(
      `${baseUrl}/v1/diagnoses/${encodeURIComponent(diagnosisId)}/notify${qs}`,
      { method: 'POST' },
    );
    if (!response.ok) {
      throw new Error(await readJsonError(response));
    }
    return (await response.json()) as DpsNotifyResponse;
  }

  async listClickUpChannels(): Promise<DpsClickUpChannelsResponse> {
    const baseUrl = await this.baseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/v1/notifications/clickup/channels`,
    );
    if (!response.ok) {
      throw new Error(await readJsonError(response));
    }
    return (await response.json()) as DpsClickUpChannelsResponse;
  }

  async listDiagnosisTimeline(
    diagnosisId: string,
    options?: { limit?: number; cursor?: string },
  ): Promise<DpsDiagnosisTimelineResponse> {
    const baseUrl = await this.baseUrl();
    const params = new URLSearchParams();
    if (typeof options?.limit === 'number') {
      params.set('limit', String(options.limit));
    }
    if (options?.cursor) {
      params.set('cursor', options.cursor);
    }
    const q = params.toString();
    const url = `${baseUrl}/v1/diagnoses/${encodeURIComponent(
      diagnosisId,
    )}/timeline${q ? `?${q}` : ''}`;
    const response = await this.fetchApi.fetch(url);
    if (!response.ok) {
      throw new Error(await readJsonError(response));
    }
    return (await response.json()) as DpsDiagnosisTimelineResponse;
  }

  async listAllDiagnosisTimeline(
    diagnosisId: string,
  ): Promise<DpsDiagnosisTimelineItem[]> {
    const merged: DpsDiagnosisTimelineItem[] = [];
    let cursor: string | undefined;
    for (;;) {
      const res = await this.listDiagnosisTimeline(diagnosisId, {
        limit: 200,
        cursor,
      });
      merged.push(...res.items);
      if (!res.next_cursor) {
        break;
      }
      cursor = res.next_cursor;
      if (merged.length > 8000) {
        break;
      }
    }
    return merged;
  }

  async listRulesetFamilies(): Promise<DpsRulesetFamiliesListResponse> {
    const baseUrl = await this.baseUrl();
    const response = await this.fetchApi.fetch(`${baseUrl}/v1/rulesets`);
    if (!response.ok) {
      throw new Error(await readJsonError(response));
    }
    return (await response.json()) as DpsRulesetFamiliesListResponse;
  }

  async getRulesetFamily(id: string): Promise<DpsRulesetFamily> {
    const baseUrl = await this.baseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/v1/rulesets/${encodeURIComponent(id)}`,
    );
    if (!response.ok) {
      throw new Error(await readJsonError(response));
    }
    return (await response.json()) as DpsRulesetFamily;
  }

  async listRulesetVersions(
    familyId: string,
  ): Promise<DpsRulesetVersionsListResponse> {
    const baseUrl = await this.baseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/v1/rulesets/${encodeURIComponent(familyId)}/versions`,
    );
    if (!response.ok) {
      throw new Error(await readJsonError(response));
    }
    return (await response.json()) as DpsRulesetVersionsListResponse;
  }

  async getRulesetVersion(
    familyId: string,
    version: number,
  ): Promise<DpsRulesetVersion> {
    const baseUrl = await this.baseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/v1/rulesets/${encodeURIComponent(
        familyId,
      )}/versions/${version}`,
    );
    if (!response.ok) {
      throw new Error(await readJsonError(response));
    }
    return (await response.json()) as DpsRulesetVersion;
  }

  async createRulesetFamily(body: {
    ruleset_family_id: string;
    description?: string;
    config?: Record<string, unknown>;
  }): Promise<DpsRulesetFamily> {
    const baseUrl = await this.baseUrl();
    const response = await this.fetchApi.fetch(`${baseUrl}/v1/rulesets`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!response.ok) {
      throw new Error(await readJsonError(response));
    }
    return (await response.json()) as DpsRulesetFamily;
  }

  async createRulesetVersion(
    familyId: string,
    body: { config: Record<string, unknown>; activate?: boolean },
  ): Promise<DpsRulesetVersion> {
    const baseUrl = await this.baseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/v1/rulesets/${encodeURIComponent(familyId)}/versions`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      },
    );
    if (!response.ok) {
      throw new Error(await readJsonError(response));
    }
    return (await response.json()) as DpsRulesetVersion;
  }

  async updateRulesetFamily(
    id: string,
    body: { active_version?: number; description?: string },
  ): Promise<DpsRulesetFamily> {
    const baseUrl = await this.baseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/v1/rulesets/${encodeURIComponent(id)}`,
      {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      },
    );
    if (!response.ok) {
      throw new Error(await readJsonError(response));
    }
    return (await response.json()) as DpsRulesetFamily;
  }

  async validateRulesetConfig(
    config: Record<string, unknown>,
  ): Promise<DpsRulesetValidationResult> {
    const baseUrl = await this.baseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/v1/rulesets/validate`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ config }),
      },
    );
    if (!response.ok) {
      throw new Error(await readJsonError(response));
    }
    return (await response.json()) as DpsRulesetValidationResult;
  }

  async getRulesetSchema(): Promise<DpsRulesetSchema> {
    const baseUrl = await this.baseUrl();
    const response = await this.fetchApi.fetch(`${baseUrl}/v1/rulesets/schema`);
    if (!response.ok) {
      throw new Error(await readJsonError(response));
    }
    return (await response.json()) as DpsRulesetSchema;
  }

  async getRulesetTemplate(): Promise<Record<string, unknown>> {
    const baseUrl = await this.baseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/v1/rulesets/template`,
    );
    if (!response.ok) {
      throw new Error(await readJsonError(response));
    }
    const data = (await response.json()) as { config: Record<string, unknown> };
    return data.config;
  }

  async replayDiagnosis(
    eventId: string,
    body: {
      ruleset_family_id: string;
      ruleset_version: number;
      include_timeline?: boolean;
    },
  ): Promise<DpsReplayResponse> {
    const baseUrl = await this.baseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/v1/events/${encodeURIComponent(eventId)}/diagnoses/replay`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      },
    );
    if (!response.ok) {
      throw new Error(await readJsonError(response));
    }
    return (await response.json()) as DpsReplayResponse;
  }

  async deleteReplayDiagnosis(diagnosisId: string): Promise<void> {
    const baseUrl = await this.baseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/v1/diagnoses/${encodeURIComponent(diagnosisId)}`,
      { method: 'DELETE' },
    );
    if (!response.ok) {
      throw new Error(await readJsonError(response));
    }
  }

  async bulkDeleteReplays(body: {
    ruleset_family_id: string;
    ruleset_version: number;
  }): Promise<{
    status: string;
    job_id: string;
    created: boolean;
    ruleset_family_id: string;
    ruleset_version: number;
  }> {
    const baseUrl = await this.baseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/v1/admin/bulk-delete-replays`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      },
    );
    if (!response.ok) {
      throw new Error(await readJsonError(response));
    }
    return (await response.json()) as {
      status: string;
      job_id: string;
      created: boolean;
      ruleset_family_id: string;
      ruleset_version: number;
    };
  }

  async purgeQueue(queue?: string): Promise<{ purged: string[] }> {
    const baseUrl = await this.baseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/v1/admin/purge-queue`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(queue ? { queue } : {}),
      },
    );
    if (!response.ok) throw new Error(await readJsonError(response));
    return (await response.json()) as { purged: string[] };
  }

  async bulkDeletePrimaries(body: {
    from: string;
    to: string;
    node_id?: string;
  }): Promise<{
    status: string;
    job_id: string;
    created: boolean;
    from: string;
    to: string;
    node_id: string | null;
  }> {
    const baseUrl = await this.baseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/v1/admin/bulk-delete-primaries`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      },
    );
    if (!response.ok) throw new Error(await readJsonError(response));
    return (await response.json()) as {
      status: string;
      job_id: string;
      created: boolean;
      from: string;
      to: string;
      node_id: string | null;
    };
  }

  async getJobStatus(jobId: string): Promise<{
    id: string;
    state: string;
    output: unknown;
    created_on: string;
    completed_on: string | null;
  } | null> {
    const baseUrl = await this.baseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/v1/admin/job-status/${encodeURIComponent(jobId)}`,
    );
    if (response.status === 404) return null;
    if (!response.ok) throw new Error(await readJsonError(response));
    return (await response.json()) as {
      id: string;
      state: string;
      output: unknown;
      created_on: string;
      completed_on: string | null;
    };
  }

  async createHost(input: DpsCreateHostInput): Promise<DpsCreateHostResponse> {
    const baseUrl = await this.baseUrl();
    const response = await this.fetchApi.fetch(`${baseUrl}/v1/hosts`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    });
    if (!response.ok) throw new Error(await readJsonError(response));
    return (await response.json()) as import('./types').DpsCreateHostResponse;
  }

  async listHosts(): Promise<DpsHostItem[]> {
    const baseUrl = await this.baseUrl();
    const response = await this.fetchApi.fetch(`${baseUrl}/v1/hosts`);
    if (!response.ok) throw new Error(await readJsonError(response));
    const data = (await response.json()) as DpsHostsListResponse;
    return data.hosts;
  }

  async updateHostRole(
    hostname: string,
    role: DpsHostRole | null,
  ): Promise<DpsHostItem> {
    const baseUrl = await this.baseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/v1/hosts/${encodeURIComponent(hostname)}`,
      {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ host_role: role }),
      },
    );
    if (!response.ok) throw new Error(await readJsonError(response));
    const data = (await response.json()) as { host: DpsHostItem };
    return data.host;
  }

  async deleteHost(
    hostname: string,
  ): Promise<{ deleted: boolean; hostname: string }> {
    const baseUrl = await this.baseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/v1/hosts/${encodeURIComponent(hostname)}`,
      { method: 'DELETE' },
    );
    if (!response.ok) throw new Error(await readJsonError(response));
    return (await response.json()) as { deleted: boolean; hostname: string };
  }

  async diagnoseNode(nodeId: string): Promise<DpsManualDiagnoseResponse> {
    const baseUrl = await this.baseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/v1/nodes/${encodeURIComponent(nodeId)}/diagnose`,
      { method: 'POST' },
    );
    if (!response.ok) throw new Error(await readJsonError(response));
    return (await response.json()) as DpsManualDiagnoseResponse;
  }

  async getService(entityRef: string): Promise<DpsServiceItem | null> {
    const baseUrl = await this.baseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/v1/services/${encodeURIComponent(entityRef)}`,
    );
    if (response.status === 404) return null;
    if (!response.ok) return null;
    return (await response.json()) as DpsServiceItem;
  }

  async getNotificationPolicies(): Promise<DpsNotificationPolicy[]> {
    const baseUrl = await this.baseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/v1/notifications/policy`,
    );
    if (!response.ok) throw new Error(await readJsonError(response));
    const data = (await response.json()) as DpsNotificationPoliciesResponse;
    return data.policies;
  }

  async updateNotificationPolicy(
    role: string,
    input: DpsNotificationPolicyUpdateInput,
  ): Promise<DpsNotificationPolicy> {
    const baseUrl = await this.baseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/v1/notifications/policy/${encodeURIComponent(role)}`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(input),
      },
    );
    if (!response.ok) throw new Error(await readJsonError(response));
    const data = (await response.json()) as {
      policy: DpsNotificationPolicy;
    };
    return data.policy;
  }

  async getNotificationPolicyOverrides(): Promise<
    DpsNotificationPolicyOverride[]
  > {
    const baseUrl = await this.baseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/v1/notifications/policy/overrides`,
    );
    if (!response.ok) throw new Error(await readJsonError(response));
    const data =
      (await response.json()) as DpsNotificationPolicyOverridesResponse;
    return data.overrides;
  }

  async upsertNotificationPolicyOverride(
    hostname: string,
    input: DpsNotificationPolicyOverrideInput,
  ): Promise<DpsNotificationPolicyOverride> {
    const baseUrl = await this.baseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/v1/notifications/policy/host/${encodeURIComponent(hostname)}`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(input),
      },
    );
    if (!response.ok) throw new Error(await readJsonError(response));
    const data = (await response.json()) as {
      override: DpsNotificationPolicyOverride;
    };
    return data.override;
  }

  async deleteNotificationPolicyOverride(hostname: string): Promise<void> {
    const baseUrl = await this.baseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/v1/notifications/policy/host/${encodeURIComponent(hostname)}`,
      { method: 'DELETE' },
    );
    if (!response.ok) throw new Error(await readJsonError(response));
  }

  async listSquadRouting(): Promise<DpsSquadRouting[]> {
    const baseUrl = await this.baseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/v1/notifications/squad-routing`,
    );
    if (!response.ok) throw new Error(await readJsonError(response));
    const data = (await response.json()) as DpsSquadRoutingListResponse;
    return data.items;
  }

  async upsertSquadRouting(
    ownerRef: string,
    input: DpsSquadRoutingUpsertInput,
  ): Promise<DpsSquadRouting> {
    const baseUrl = await this.baseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/v1/notifications/squad-routing/${encodeURIComponent(
        ownerRef,
      )}`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(input),
      },
    );
    if (!response.ok) throw new Error(await readJsonError(response));
    const data = (await response.json()) as { item: DpsSquadRouting };
    return data.item;
  }

  async deleteSquadRouting(ownerRef: string): Promise<void> {
    const baseUrl = await this.baseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/v1/notifications/squad-routing/${encodeURIComponent(
        ownerRef,
      )}`,
      { method: 'DELETE' },
    );
    if (!response.ok) throw new Error(await readJsonError(response));
  }

  async listSquadRoutingClickUpChannels(): Promise<DpsSquadRoutingClickUpChannelsResponse> {
    const baseUrl = await this.baseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/v1/notifications/squad-routing/clickup-channels`,
    );
    if (!response.ok) throw new Error(await readJsonError(response));
    return (await response.json()) as DpsSquadRoutingClickUpChannelsResponse;
  }

  async getServerHealth(
    eventId: string,
  ): Promise<DpsServerHealthSnapshot | null> {
    const baseUrl = await this.baseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/v1/events/${encodeURIComponent(eventId)}/server-health`,
    );
    if (response.status === 503) return null;
    if (!response.ok) return null;
    return (await response.json()) as DpsServerHealthSnapshot;
  }

  async getEventPipeline(eventId: string): Promise<DpsPipelineFetchResult> {
    const baseUrl = await this.baseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/v1/event-pipeline/${encodeURIComponent(eventId)}`,
    );
    if (response.ok) {
      const body = (await response.json()) as {
        pipeline: DpsPipelineSnapshot;
        phases: DpsPipelinePhaseView[];
      };
      return { status: 'ok', pipeline: body.pipeline, phases: body.phases };
    }
    if (response.status === 404) {
      return { status: 'not_found' };
    }
    if (response.status === 503) {
      return {
        status: 'unavailable',
        error: await readJsonError(response),
      };
    }
    return { status: 'error', error: await readJsonError(response) };
  }
}
